import type { CSSProperties } from 'react'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { Heart, MessageCircle, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { LogoMark } from '@/components/generals/logo'

interface HeroPortraitCardProps {
    className?: string
}

const SEGMENTS = 3
const SEGMENT_MS = 4200

/** Story-style portrait card in full color that floats up last in the hero. */
export const HeroPortraitCard = ({ className }: HeroPortraitCardProps) => {
    const reduced = useReducedMotion()
    const [liked, setLiked] = useState(false)
    const [active, setActive] = useState(0)

    useEffect(() => {
        if (reduced) return

        const timer = setInterval(() => setActive(current => (current + 1) % SEGMENTS), SEGMENT_MS)

        return () => clearInterval(timer)
    }, [reduced])

    return (
        <div className={cn('motion-rise [--rise-delay:1900ms]', className)}>
            <div className='motion-float [animation-delay:3.4s] [animation-duration:8s]'>
                <article className='grain relative h-[460px] w-[min(84vw,330px)] overflow-hidden rounded-[30px] bg-ink shadow-[0_60px_120px_-40px_rgba(244,170,100,0.55)] sm:h-[500px] sm:w-[360px]'>
                    <Image
                        src='/images/uploads/feature-clean.png'
                        alt='A Verdane member in soft golden light'
                        fill
                        sizes='360px'
                        priority
                        className='object-cover saturate-[1.1]'
                        style={{ objectPosition: '55% 20%' }}
                    />
                    <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-3/5 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(40,16,6,0.35)_50%,rgba(20,8,4,0.78)_100%)]' />
                    <div aria-hidden='true' className='hairline pointer-events-none absolute inset-0 rounded-[inherit]' />

                    <div className='absolute inset-x-4 top-4 z-10'>
                        <div aria-hidden='true' className='flex gap-1.5'>
                            {Array.from({ length: SEGMENTS }, (_, index) => (
                                <span key={index} className='h-[3px] flex-1 overflow-hidden rounded-full bg-white/30'>
                                    <span
                                        className={cn('block h-full rounded-full bg-white', index === active && !reduced && 'motion-story')}
                                        style={{ width: index < active || (reduced && index === 0) ? '100%' : index === active ? undefined : '0%', '--story-ms': `${SEGMENT_MS}ms` } as CSSProperties}
                                    />
                                </span>
                            ))}
                        </div>

                        <div className='mt-3 flex items-center justify-between'>
                            <span className='flex items-center gap-2 text-[13px] font-medium text-cream'>
                                <span className='grid size-7 place-items-center rounded-full bg-black/80'>
                                    <LogoMark className='size-3.5 text-cream' />
                                </span>
                                Verdane
                            </span>

                            <Link href='/what-we-do' aria-label='Discover what Verdane does' className='grid size-8 place-items-center rounded-full text-cream/90 transition-colors hover:bg-white/10'>
                                <MoreHorizontal className='size-4' />
                            </Link>
                        </div>
                    </div>

                    <div className='absolute inset-x-5 bottom-5 z-10'>
                        <p className='text-[2.6rem] font-semibold leading-[0.95] tracking-[-0.045em] text-cream'>
                            Tending <span className='em-serif font-normal'>your</span>
                            <br />
                            <span className='font-serif font-normal italic'>future</span>
                        </p>

                        <div className='mt-5 flex items-center gap-2'>
                            <span className='glass inline-flex h-10 items-center rounded-full px-4 text-[13px] font-medium text-cream'>
                                Member favorite
                            </span>

                            <button
                                type='button'
                                onClick={() => setLiked(value => !value)}
                                aria-pressed={liked}
                                aria-label={liked ? 'Remove from favorites' : 'Add to favorites'}
                                className='glass grid size-10 place-items-center rounded-full text-cream transition-transform duration-300 active:scale-90'
                            >
                                <Heart className={cn('size-4 transition-colors duration-300', liked && 'fill-citron text-citron')} />
                            </button>

                            <Link
                                href='/ai-intelligence#demo'
                                aria-label='Put a question to your AI advisor'
                                className='glass grid size-10 place-items-center rounded-full text-cream transition-transform duration-300 hover:scale-105'
                            >
                                <MessageCircle className='size-4' />
                            </Link>
                        </div>
                    </div>
                </article>
            </div>
        </div>
    )
}
